/**
 * @fileoverview Canonical catalog of document merge-field tokens (the single source for the
 * builder hint, a future merge-field picker, and documentation) — G-CONST.
 * @module document-render/documentMergeFields
 *
 * Responsibilities: - Enumerate the merge-field tokens the worker resolves, with human labels
 *                     and grouping, so the list is defined once instead of inlined per page.
 * Dependencies:     - none (pure data).
 * Architecture:     - Framework-agnostic constant; mirrors the worker MERGE_FIELD_RESOLVERS keys.
 * Notes:            - When a resolver token is added/removed in the worker, update this catalog.
 */

export type MergeFieldCategory = 'Property/Owner' | 'Address' | 'Inspection' | 'Inspector' | 'Department' | 'Document';

/**
 * @contract MergeFieldDescriptor
 * Purpose: One merge-field token the author can insert into document text.
 * Fields:  token (without braces), label, category.
 * Rules:   `token` must match a worker MERGE_FIELD_RESOLVERS key.
 * Sync:    N/A (static catalog).
 */
export interface MergeFieldDescriptor {
  token: string;
  label: string;
  category: MergeFieldCategory;
}

export const DOCUMENT_MERGE_FIELDS: MergeFieldDescriptor[] = [
  { token: 'ownerName', label: 'Owner / Responsible Party', category: 'Property/Owner' },
  { token: 'entityName', label: 'Property Name', category: 'Property/Owner' },
  { token: 'entityCategory', label: 'Property Category', category: 'Property/Owner' },
  { token: 'parcelNo', label: 'Parcel Number', category: 'Property/Owner' },
  { token: 'apn', label: 'APN', category: 'Property/Owner' },
  { token: 'inspectionAddress', label: 'Inspection Address', category: 'Address' },
  { token: 'mailingAddress', label: 'Mailing Address', category: 'Address' },
  { token: 'mailingStreet', label: 'Mailing Street', category: 'Address' },
  { token: 'mailingCity', label: 'Mailing City', category: 'Address' },
  { token: 'mailingState', label: 'Mailing State', category: 'Address' },
  { token: 'mailingZip', label: 'Mailing ZIP', category: 'Address' },
  { token: 'contactNumber', label: 'Contact Number', category: 'Property/Owner' },
  { token: 'emailAddress', label: 'Email Address', category: 'Property/Owner' },
  { token: 'inspectionType', label: 'Inspection Type', category: 'Inspection' },
  { token: 'inspectionDate', label: 'Inspection Date', category: 'Inspection' },
  { token: 'inspectionNumber', label: 'Inspection Number', category: 'Inspection' },
  { token: 'complianceStatus', label: 'Compliance Status', category: 'Inspection' },
  { token: 'violationCount', label: 'Violation Count', category: 'Inspection' },
  { token: 'comments', label: 'Inspection Comments', category: 'Inspection' },
  { token: 'inspectorName', label: 'Inspector Name', category: 'Inspector' },
  { token: 'inspectorTitle', label: 'Inspector Title', category: 'Inspector' },
  { token: 'inspectorBadge', label: 'Inspector Badge #', category: 'Inspector' },
  { token: 'departmentName', label: 'Department Name', category: 'Department' },
  { token: 'departmentAddress', label: 'Department Address', category: 'Department' },
  { token: 'departmentPhone', label: 'Department Phone', category: 'Department' },
  { token: 'departmentFax', label: 'Department Fax', category: 'Department' },
  { token: 'departmentEmail', label: 'Department Email', category: 'Department' },
  { token: 'departmentWebsite', label: 'Department Website', category: 'Department' },
  { token: 'reportNumber', label: 'Report Number', category: 'Document' },
  { token: 'date', label: 'Document Date', category: 'Document' },
  { token: 'year', label: 'Year', category: 'Document' },
];

/** Render a token with braces, e.g. `mergeFieldToken('date') === '{{date}}'`. */
export function mergeFieldToken(token: string): string {
  return `{{${token}}}`;
}

/** A short comma-separated hint of the most common merge fields (for builder placeholders). */
export function mergeFieldHint(limit = 6): string {
  return DOCUMENT_MERGE_FIELDS.slice(0, limit).map((f) => mergeFieldToken(f.token)).join(', ');
}
